/**
 * TemplatesPage — /agents/templates
 * Agent templates on the ListPage template. Demo page: /tables/data-tables (toolbar with search, quick filter and scoped
 * selects above one table; row ⋯ menu). Search is local and also asks the marketplace (entity-management §1.3, §5).
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Plus } from 'lucide-react';
import {
  deleteTemplateMutation,
  listTemplatesOptions,
  listTemplatesQueryKey,
  setTemplateStatusMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import {
  Callout,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FilterPills,
  PublishToMarketplaceModal,
  SearchBar,
  Toolbar,
  describeQueryError,
  useToast,
} from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { Can } from '../../components/auth/Can.js';
import { MarketplaceResults } from '../../components/entity/MarketplaceResults.js';
import { MarketplaceDetailSheet } from '../../components/entity/MarketplaceDetailSheet.js';
import type { MarketplaceListing } from '../../services/api/marketplace.api.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { PAGE_HELP } from '../../help/index.js';
import { FilterSelect, facetOptions } from '../../components/ui/FilterSelect.js';
import { buildTemplateColumns, templateDepartment, templateStatus, type TemplateRow } from './TemplateTableColumns.js';

type StatusPill = 'active' | 'disabled' | 'all';

function matchesSearch(row: TemplateRow, query: string): boolean {
  if (!query) return true;
  const haystack = `${row.title} ${row.slug} ${row.goal ?? ''} ${templateDepartment(row)} ${row.platform ?? ''}`;
  return haystack.toLowerCase().includes(query);
}

export const TemplatesPage: React.FC = () => {
  const help = PAGE_HELP['templates'];
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const canPublish = usePolicy('CanManageWorkflows');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusPill>('active');
  const [department, setDepartment] = useState('');
  const [source, setSource] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TemplateRow | null>(null);
  const [publishTarget, setPublishTarget] = useState<TemplateRow | null>(null);
  const [marketplaceTarget, setMarketplaceTarget] = useState<MarketplaceListing | null>(null);

  // All templates, disabled included: the status pills decide what shows.
  const templatesQuery = useQuery(listTemplatesOptions({ query: { includeDisabled: true } }));
  const rows = templatesQuery.data?.data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: listTemplatesQueryKey() });
  const setStatusMutation = useMutation(setTemplateStatusMutation());
  const deleteMutation = useMutation(deleteTemplateMutation());

  const toggleStatus = async (row: TemplateRow) => {
    const disabling = !row.disabled;
    try {
      const result = await setStatusMutation.mutateAsync({
        path: { templateSlug: row.slug },
        body: { status: disabling ? 'disabled' : 'active' },
      });
      const dependents = result.dependentWorkflows;
      if (disabling && dependents.length > 0) {
        const active = dependents.filter((w) => w.status === 'active').map((w) => w.name);
        const draft = dependents.filter((w) => w.status !== 'active').map((w) => w.name);
        const parts = [`Disabled "${row.title}".`];
        if (active.length > 0) parts.push(`Active workflows will fail if triggered: ${active.join(', ')}.`);
        if (draft.length > 0) parts.push(`Draft workflows: ${draft.join(', ')}.`);
        notify(parts.join(' '), 'info');
      }
      await refresh();
    } catch (error) {
      notify(
        `Could not ${disabling ? 'disable' : 'enable'} the template: ${describeQueryError(error, 'template')}`,
        'danger',
      );
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ path: { templateSlug: deleteTarget.slug } });
      await refresh();
      notify(`Deleted "${deleteTarget.title}"`);
      setDeleteTarget(null);
    } catch (error) {
      notify(describeQueryError(error, 'template'), 'danger');
    }
  };

  const query = search.trim().toLowerCase();
  const visible = rows.filter((row) => {
    if (!matchesSearch(row, query)) return false;
    if (status === 'active' && row.disabled) return false;
    if (status === 'disabled' && !row.disabled) return false;
    if (department && templateDepartment(row) !== department) return false;
    if (source && row.source !== source) return false;
    return true;
  });

  const pills = [
    { id: 'active', label: 'Active', count: rows.filter((r) => templateStatus(r) === 'active').length },
    { id: 'disabled', label: 'Disabled', count: rows.filter((r) => r.disabled).length },
    { id: 'all', label: 'All', count: rows.length },
  ];

  const columns = buildTemplateColumns({
    canPublish,
    onOpen: (row) => navigate(`/agents/templates/${row.slug}`),
    onToggleStatus: toggleStatus,
    onDelete: setDeleteTarget,
    onPublish: setPublishTarget,
  });

  const newTemplateLink = (
    <Can policy="CanManageAgentTemplates">
      <Link to="/agents/templates/new" className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}>
        <Plus size={14} aria-hidden="true" />
        New template
      </Link>
    </Can>
  );

  const usedBy = deleteTarget?.workflowCount ?? 0;

  return (
    <>
      <ListPage
        title="Templates"
        subtitle="Reusable agent profiles: persona, instructions and tools. Workflow steps and employees pick one."
        breadcrumbs={[{ label: 'Libraries', to: '/agents/libraries' }, { label: 'Templates' }]}
        helpContent={help}
        actions={newTemplateLink}
        alert={
          templatesQuery.isError ? (
            <Callout tone="danger" title="Could not load templates">
              {describeQueryError(templatesQuery.error, 'templates')}
            </Callout>
          ) : undefined
        }
        toolbar={
          <Toolbar
            search={<SearchBar value={search} onChange={setSearch} placeholder="Search name, goal, platform…" />}
            pills={
              <FilterPills
                aria-label="Status"
                options={pills}
                value={status}
                onChange={(id) => setStatus(id as StatusPill)}
              />
            }
            right={
              <>
                <FilterSelect
                  label="Department"
                  anyLabel="All departments"
                  value={department}
                  options={facetOptions(rows, templateDepartment)}
                  onChange={setDepartment}
                />
                <FilterSelect
                  label="Source"
                  anyLabel="All sources"
                  value={source}
                  options={facetOptions(rows, (row) => row.source)}
                  onChange={setSource}
                />
              </>
            }
          />
        }
      >
        {!templatesQuery.isError && (
          <DataTable
            aria-label="Templates"
            columns={columns}
            rows={visible}
            rowKey={(row) => row.id}
            loading={templatesQuery.isPending}
            pageSize={25}
            empty={
              <EmptyState
                icon={Bot}
                title={rows.length === 0 ? (help?.emptyState.title ?? 'No templates yet') : 'No templates match'}
                description={
                  rows.length === 0
                    ? (help?.emptyState.description ?? 'Create a template to define how an agent works.')
                    : 'Change the search or filters to see more templates.'
                }
                learnItems={rows.length === 0 ? help?.emptyState.learnItems : undefined}
                action={rows.length === 0 ? newTemplateLink : undefined}
              />
            }
          />
        )}
      </ListPage>

      <MarketplaceResults
        entityType="agent_template"
        search={search}
        label="templates"
        onSelect={setMarketplaceTarget}
      />
      <MarketplaceDetailSheet listing={marketplaceTarget} onClose={() => setMarketplaceTarget(null)} />

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete template"
        message={
          deleteTarget
            ? usedBy > 0
              ? `Delete "${deleteTarget.title}"? ${usedBy} ${usedBy === 1 ? 'workflow uses' : 'workflows use'} it and will stop working.`
              : `Delete "${deleteTarget.title}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete template"
        loading={deleteMutation.isPending}
      />

      {publishTarget && (
        <PublishToMarketplaceModal
          entityType="agent_template"
          entityId={publishTarget.slug}
          entityName={publishTarget.title}
          open
          onClose={() => setPublishTarget(null)}
        />
      )}
    </>
  );
};
